import { useState, useEffect } from 'react';
import { InputField, RadioField, CheckboxField, TextareaField } from '@/components/formParts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { fetchPurposeOptions, fetchDenshijitiOptions, MasterOption,fetchMailOptOptions} from '@/data';
import { removeEmoji, zenkakuToHankaku } from '@/utiils';
import { getAddress } from "jposta";

// バリデーション用のスキーマ設定
const schemaBase = z.object({
  zip_current: z.string().min(1, { message: "郵便番号は必須です" }).regex(/^\d{3}-?\d{4}$/, { message: "正しい形式で入力してください" }),
  address_current: z.string().min(1, { message: "住所は必須です" }),
  amc: z.string().regex(/^(|\d{10})$/, { message: "ANAマイレージクラブお客様番号は10桁の半角数字で入力してください" }),
  phone: z.string().min(1, { message: "電話番号は必須です" }).regex(/^0[-\d]{9,12}$/, { message: "正しい形式で入力してください" }),
  // city: z.string().min(1, { message: "希望自治体は必須です" }),
  // purpose_detail: z.string().min(1, { message: "具体的な目的は必須です" })
  //   .min(20, { message: "具体的な目的は20〜80文字で入力してください" })
  //   .max(80, { message: "具体的な目的は20〜80文字で入力してください" })
  //   .regex(/^(?!.*(?:<>|<[^>]*>|https?:|www\.|ftp:))[\s\S]*$/, { message: "URL、絵文字、HTMLタグは使用できません" }),
  purpose_detail: z.string().regex(/^(?!.*(?:<>|<[^>]*>|https?:|www\.|ftp:))[\s\S]*$/, { message: "URL、絵文字、HTMLタグは使用できません" }),  
  condition_checked: z.boolean().refine((val) => val !== false, { message: "承諾チェックは必須です" }),
  mail_opt: z.string().min(1, { message: "二地域居住にとっておすすめのメール配信を希望するは必須です" }),
});

const purposeSchema = z.object({
  purpose: z.array(z.string()).min(1, { message: "二地域居住の目的は必須です" }).max(8, { message: "二地域居住の目的は1〜8つまで選択できます" }),
  purpose_other: z.string()
    .regex(/^(?!.*(?:<>|<[^>]*>|https?:|www\.|ftp:)).*$/, { message: "URL、絵文字、HTMLタグは使用できません" })
    .optional(),
}).superRefine((data, ctx) => {
  if (data.purpose.includes('99') && !data.purpose_other) {
    ctx.addIssue({
      code: 'custom',
      message: 'その他を選択の場合、記入は必須です',
      path: ['purpose_other'],
    });
  }
  // if (data.purpose.includes('99') && ((data.purpose_other ?? '').length < 81 || (data.purpose_other ?? '').length > 19)) {
  //   ctx.addIssue({
  //     code: 'custom',
  //     message: 'その他の内容は20〜80文字で入力してください',
  //     path: ['purpose_other'],
  //   });
  // }
  if (data.purpose?.includes("99")) {
    const v = (data.purpose_other ?? "").trim();

    if (v.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["purpose_other"],
        message: "その他の内容は必須です",
      });
    } else if (v.length < 20 || v.length > 80) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["purpose_other"],
        message: "その他の内容は20〜80文字で入力してください",
      });
    }
  } 

});

const schema = schemaBase.and(purposeSchema);

// バリデーション用の型定義
export type RegisterRequestFormData = z.infer<typeof schema>;


// フォームコンポーネント
const RegisterRequesttForm = ( { enabled, onValidationChange, shouldSubmit }: {
    enabled: boolean;
    onValidationChange: (data: {isValid: boolean, formData: RegisterRequestFormData | undefined}) => void;
    shouldSubmit: boolean;
  }
) => {

  const { register, setValue, watch, handleSubmit, formState: { errors, isValid }, trigger } = useForm<RegisterRequestFormData>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
  });

  const [purposeOptions, setPurposeOptions] = useState<MasterOption[]>([]);
  //const [cityOptions, setCityOptions] = useState<MasterOption[]>([]);
  const [mailOptOptions, setMailOptOptions] = useState<MasterOption[]>([]);
  // データ取得
  useEffect(() => {
    const loadData = async () => {
      try {
        const [purpose, city,mailOpts] = await Promise.all([
          fetchPurposeOptions(),
          fetchDenshijitiOptions(),
          fetchMailOptOptions()
        ]);
        setPurposeOptions(purpose);
        setMailOptOptions(mailOpts);
        //setCityOptions(city);
      } catch (error) {
        console.error('データ取得エラー:', error);
      }
    };
    loadData();
  }, []);

  // valueの初期化
  useEffect(() => {
    setValue('purpose', []);
  }, []);

  // 郵便番号から住所を取得
  const zipCode = watch('zip_current');
  useEffect(() => {
    const getZipAddress = async () => {
      if( zipCode && zipCode.match(/^\d{3}-?\d{4}$/) ) {
        const address = await getAddress(zipCode);
        if( !!address ) {
          setValue('address_current', address.pref + address.city + address.area);
          trigger('address_current');
        }
      }
    }
    getZipAddress();
  }, [zipCode]);

  // 親にバリデーション状態を通知
  useEffect(() => {
    onValidationChange({
      isValid,
      formData: isValid ? {
        ...watch(),
        zip_current: watch('zip_current')?.replace(/-/g, ''),
        phone: watch('phone')?.replace(/-/g, ''),
      } : undefined});
  }, [isValid]);

  // 親からのsubmit要求があったらバリデーションを実行
  useEffect(() => {
    if( shouldSubmit ) {
      trigger();
    }
  }, [shouldSubmit]);

    // valueの初期化
    useEffect(() => {
      setValue('mail_opt', '1', { shouldValidate: true, shouldDirty: false });
    }, [setValue]);
  // purpose変更時のバリデーション発火
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'purpose') {
        if (!value.purpose || !Array.isArray(value.purpose) || (!value.purpose.includes('99'))) {
          trigger('purpose_other');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger])
  const purpose = watch('purpose');
  const showOther = Array.isArray(purpose) && purpose.includes('99');
  return (
    <>
      <div className="xmd:max-w-[40rem]">
        <InputField label="郵便番号" type="text" register={register('zip_current')} errors={errors} hasRequiredLabel={true} disabled={!enabled} onInput={(e) => zenkakuToHankaku(e.target as HTMLInputElement)} />
        <p style={{ marginTop: 4, marginLeft: 10, lineHeight: 1.2 }}><small>※ハイフンは不要です</small></p>
      </div>
      <InputField label="住所" type="text" register={register('address_current')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      <div className="xmd:max-w-[40rem]">
        <InputField label="電話番号" type="text" register={register('phone')} errors={errors} hasRequiredLabel={true} disabled={!enabled} onInput={(e) => zenkakuToHankaku(e.target as HTMLInputElement)} />
      </div>
      <div className="xmd:max-w-[40rem]">
        <InputField label="ANAマイレージクラブお客様番号" type="text" register={register('amc')} errors={errors} disabled={!enabled} onInput={(e) => zenkakuToHankaku(e.target as HTMLInputElement)} />
      </div>
      <RadioField label="二地域居住にとっておすすめのメール配信を希望する" options={mailOptOptions} register={register('mail_opt')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      {/* <div className="xmd:max-w-[40rem]">
        <SelectField label="希望二地域居住自治体" options={cityOptions} placeholder="選択してください" register={register('city')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      </div> */}
      {/* <div className="flex flex-col gap-1 checkbox-group">
        <CheckboxField label="二地域居住の目的" options={purposeOptions} register={register('purpose')} errors={errors} hasRequiredLabel={true} disabled={!enabled} 　/>
        {watch('purpose') && Array.isArray(watch('purpose')) && watch('purpose').includes('99') && (
          <InputField type="text" placeholder="その他の場合" register={register('purpose_other')} errors={errors} hasRequiredLabel={true} disabled={!enabled} onInput={(e) => removeEmoji(e.target as HTMLInputElement)}  />
        )}
      </div> */}

      {/* <div className="flex flex-col gap-1 checkbox-group"> */}
      <div className="check_group_list">
        <CheckboxField
          label="二地域居住の目的"
          options={purposeOptions}
          register={register('purpose')}
          errors={errors}
          hasRequiredLabel={true}
          disabled={!enabled}
        />
        {showOther && (
          <>
            <InputField
              type="text"
              placeholder="その他の場合"
              register={register('purpose_other')}
              errors={errors}
              hasRequiredLabel={true}
              disabled={!enabled}
              onInput={(e) => removeEmoji(e.target as HTMLInputElement)}
            />
            <p style={{ marginTop: 4, marginLeft: 10, lineHeight: 1.2 }}>
              <small>※20文字以上80文字以内でご記入ください</small>
            </p>
          </>
        )}
      </div>
      <TextareaField label="具体的な目的" register={register('purpose_detail')} rows={3} errors={errors} hasRequiredLabel={false} disabled={!enabled} onInput={(e) => removeEmoji(e.target as HTMLInputElement)} />
      {/* <p style={{ marginTop: -20, marginLeft: 10,lineHeight: 1.2 }}>
        <small>※20文字以上80文字以内でご記入ください</small>
      </p> */}
      <div className="flex flex-col gap-4 mt-8">
        <p className="m-form-title mb-2 mb-2">申請前チェックポイント <span className="required">*</span></p>
        <p>帰省・単身赴任・介護等を目的とした利用登録はお断りをさせて頂いております。<br></br>以下の項目をご確認ください：</p>
        <ul className="list-disc list-inside pl-4">
          <li>帰省等の利用では無い</li>
          <li>単身赴任ではない（二地域居住先に持ち家等はない）</li>
          <li>二地域居住の希望理由が介護事由ではない</li>
        </ul>
        <CheckboxField options={[{ label: '上記をすべて確認し、問題ないことを承諾します', value: '' }]} register={register('condition_checked')} errors={errors} hasRequiredLabel={true} disabled={!enabled} />
      </div>
    </>
    
  );
  
}

export default RegisterRequesttForm;
